import { BarPlot } from '@mui/x-charts/BarChart';
import { axisClasses } from '@mui/x-charts/ChartsAxis';
import { ChartsGrid } from '@mui/x-charts/ChartsGrid';
import { ChartsReferenceLine } from '@mui/x-charts/ChartsReferenceLine';
import type { ChartsAxisContentProps } from '@mui/x-charts/ChartsTooltip';
import { ChartsTooltip } from '@mui/x-charts/ChartsTooltip';
import { ChartsXAxis } from '@mui/x-charts/ChartsXAxis';
import { ChartsYAxis } from '@mui/x-charts/ChartsYAxis';
import { LineHighlightPlot, LinePlot, MarkPlot } from '@mui/x-charts/LineChart';
import { ResponsiveChartContainer } from '@mui/x-charts/ResponsiveChartContainer';
import type { GetRevenueChartDataResponse } from '@weft/api-definition';
import { dayjs, extractValues, isNotNullOrUndefined, keys } from '@weft/utils';
import { formatNumberAbbreviated } from 'apps/app/src/utils/number';
import { useMemo, useState } from 'react';
import { RevenueChartBarElement } from './revenue-bar-chart-bar-element';
import { RevenueBarChartToolTip } from './revenue-bar-chart-tooltip';

const BAR_SERIES_IDS = Object.freeze({
  Usage: 'usage',
  Fixed: 'fixed',
});

type RevenueBarChartProps = {
  revenueChartData: GetRevenueChartDataResponse['revenueChartData'];
  deferredStartDate: Date;
};

export const RevenueBarChart: React.FC<RevenueBarChartProps> = ({ deferredStartDate, revenueChartData: { fixedRevenuePerMonth, usageRevenuePerMonth, deferredRevenuePerMonth } }) => {
  const [barWidth, setBarWidth] = useState<number | undefined>(undefined);
  const dayInMonthRatio = useMemo(() => getCurrentMonthDaysRatio(deferredStartDate), [deferredStartDate]);

  return (
    <ResponsiveChartContainer
      height={400}
      margin={{ left: 50, top: 25, right: 0 }}
      sx={{
        [`.${axisClasses.root}`]: {
          [`.${axisClasses.tick}, .${axisClasses.line}`]: {
            stroke: 'none',
          },
        },
      }}
      xAxis={[{
        scaleType: 'band',
        data: keys(usageRevenuePerMonth),
        valueFormatter: (value: string, context) => context.location === 'tick' ? getXAxisLabel(value) : value,
        tickLabelStyle: {
          fontFamily: 'OakesGrotesk',
        },
      }]}
      yAxis={[{
        valueFormatter: (value: number) => formatNumberAbbreviated(value),
        tickLabelStyle: {
          fill: '#1B3D5599',
          fontFamily: 'OakesGrotesk',
        },
      }]}
      series={[{
        id: BAR_SERIES_IDS.Fixed,
        type: 'bar',
        stack: 'revenue',
        data: extractValues(fixedRevenuePerMonth, 'scheduledCharge'),
        label: 'Fixed Revenue',
        color: '#B6BABE',
      },
      {
        id: BAR_SERIES_IDS.Usage,
        type: 'bar',
        stack: 'revenue',
        data: extractValues(usageRevenuePerMonth, 'scheduledCharge'),
        label: 'Usage Revenue',
        color: '#E3E4E5',
      },
      {
        type: 'line',
        data: extractValues(deferredRevenuePerMonth, 'scheduledCharge'),
        label: 'Deferred Revenue',
        showMark: false,
        color: '#C250FF',
      }]}
    >
      <BarPlot
        borderRadius={8}
        slots={{
          bar: (props) => (
            <RevenueChartBarElement
              {...props}
              onClick={(newBarWidth) => setBarWidth(newBarWidth)}
              deferredStartDate={deferredStartDate}
              dayInMonthRatio={dayInMonthRatio}
            />
          ),
        }}
      />
      {isNotNullOrUndefined(barWidth) && (
        <ChartsReferenceLine
          x={keys(usageRevenuePerMonth)[deferredStartDate.getMonth()]}
          lineStyle={{
            translate: (barWidth * dayInMonthRatio) + 'px',
            stroke: '#B6BBBE',
            zIndex: 1,
          }}
        />
      )}
      <LinePlot />
      <ChartsXAxis />
      <ChartsYAxis />
      <MarkPlot />
      <ChartsGrid horizontal />
      <LineHighlightPlot />
      <ChartsTooltip
        trigger="axis"
        slots={{
          axisContent: (props) => (
            <RevenueBarChartToolTip {...props as ChartsAxisContentProps}/>
          ),
        }}
      />
    </ResponsiveChartContainer>
  );
};

function getXAxisLabel(value: string) {
  const year = dayjs().format('YY');

  return `${value.substring(0, 3)}, ${year}`;
}

function getCurrentMonthDaysRatio(date: Date) {
  const today = dayjs();
  const maxMonthDays = today.daysInMonth();
  const inputMonthDay = dayjs(date).date();

  return inputMonthDay / maxMonthDays;
}